import { useRef, useState } from 'react';
import { api, ApiError } from '../lib/api.js';

/**
 * Two-step attachment upload:
 *   1. POST /attachments to reserve an id.
 *   2. PUT  /attachments/:id/blob with the raw file body.
 *
 * On completion the parent gets the attachment id. Nothing is written
 * to the target entity (expense/asset/etc.) until the parent form saves.
 */

const ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']);
const MAX_BYTES = 10 * 1024 * 1024;

export function AttachmentField({
  entityType,
  value,
  onChange,
}: {
  entityType: 'expense' | 'asset' | 'rent_payment' | 'electricity_bill';
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<'idle' | 'uploading' | 'done' | 'error'>(
    value ? 'done' : 'idle',
  );
  const [error, setError] = useState<string | null>(null);

  async function onPick(file: File) {
    setError(null);
    if (!ALLOWED.has(file.type)) {
      setError('Allowed: JPEG, PNG, WebP, PDF');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('File exceeds 10 MB');
      return;
    }
    setState('uploading');
    try {
      const { attachment } = await api<{ attachment: { id: string } }>(
        '/api/v1/attachments',
        {
          method: 'POST',
          body: {
            entityType,
            mime: file.type,
            sizeBytes: file.size,
            filename: file.name,
          },
        },
      );
      await putBlob(attachment.id, file);
      onChange(attachment.id);
      setState('done');
    } catch (e) {
      setState('error');
      setError(e instanceof ApiError ? e.message : 'Upload failed');
    }
  }

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onPick(f);
        }}
      />
      {state === 'done' && value ? (
        <>
          <span className="text-xs text-emerald-700 dark:text-emerald-400">Attached ✓</span>
          <button
            type="button"
            className="text-xs underline decoration-dotted"
            onClick={() => {
              onChange(null);
              setState('idle');
              if (inputRef.current) inputRef.current.value = '';
            }}
          >
            Remove
          </button>
        </>
      ) : (
        <button
          type="button"
          disabled={state === 'uploading'}
          onClick={() => inputRef.current?.click()}
          className="rounded border border-slate-300 px-2 py-1 text-xs dark:border-slate-600"
        >
          {state === 'uploading' ? 'Uploading…' : '+ Receipt / bill'}
        </button>
      )}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}

async function putBlob(id: string, file: File): Promise<void> {
  const url = `${import.meta.env.VITE_API_URL ?? 'http://localhost:8787'}/api/v1/attachments/${id}/blob`;
  const res = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': file.type },
    body: file,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `Upload failed (${res.status})`);
  }
}
